import React, { CSSProperties, useMemo } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';

import { FormikValues } from 'formik';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';

// components
import BasicButton from '../../buttons/basicButton';
import { SearchableSelectType } from '../../inputs/searchableSelect';
import AampeCreateSelectDropdown from '../../shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import Divider from '../../shared/Divider';
import FilterOption from './FilterSelectOption';
//context
import { UseTitleContext } from './FilterTitleContext';
import FilterValueContainer from './FilterValueContainer';

interface AampeFormulasFilterSelectProps {
    name: string;
    title?: string;
    options: SearchableSelectType[];
    value?: SearchableSelectType[];
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
    ) => void;
    values?: FormikValues;
    isSortOption?: boolean;
    customContainerStyle?: CSSProperties;
    overrideSelectStyle?: StylesConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    actionButtonName?: string;
    onChange?: (values: any) => void;
    isBorderless?: boolean;
}
const customStyles: StylesConfig<
    SearchableSelectType,
    boolean,
    GroupBase<SearchableSelectType>
> = {
    control: (provided) => ({
        ...provided,
        boxShadow: 'none',
        border: 'none',
        boxSizing: 'border-box',
        minHeight: '34px',
        display: 'flex',
        alignItems: 'center',
        padding: aampeTheme.spacing4(0, 2, 0, 3),
        flexWrap: 'nowrap',
    }),
    valueContainer: (provided) => ({
        ...provided,
        display: 'flex',
        padding: 0,
        cursor: 'pointer',
        color: aampeTheme.colors.neutral09,
        fontSize: aampeTheme.size.font.default,
        fontWeight: aampeTheme.weights.light,
        flexShrink: 1,
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: aampeTheme.spacing4(0, 2),
    }),
    menu: (provided) => ({
        ...provided,
        maxWidth: '288px',
        minWidth: '240px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
        padding: aampeTheme.spacing4(2),
    }),
    option: (provided) => ({
        ...provided,
        backgroundColor: aampeTheme.colors.white,
        '&:hover': {
            backgroundColor: aampeTheme.colors.interactiveWG01,
        },
        padding: aampeTheme.spacing4(2),
    }),
};

const FilterSelect: React.FC<
    React.PropsWithChildren<AampeFormulasFilterSelectProps>
> = ({
    title,
    options,
    name,
    setFieldValue,
    values,
    isSortOption,
    overrideSelectStyle,
    actionButtonName = 'See messages',
    onChange,
    isBorderless,
}) => {
    //TODO  go through an made sure values is always selected this way
    const selected = useMemo<SearchableSelectType[]>(() => {
        if (values?.[name]) {
            if (values[name].length === 1 && values[name][0] === 'All') {
                return [];
            }

            return options
                .filter((item) => values[name].includes(item.value))
                .map((item) => {
                    return {
                        label: item.label,
                        value: item.value,
                    };
                });
        }
        return [];
    }, [values, name, options]);

    return (
        <UseTitleContext.Provider value={title ?? ''}>
            <Container isBorderless={isBorderless} data-name={name}>
                <StyledSearchableSelect
                    components={{
                        ValueContainer: FilterValueContainer,
                        Option: (innerProps) => (
                            <FilterOption
                                {...innerProps}
                                setFieldValue={setFieldValue}
                                columnId={name}
                                values={values}
                            />
                        ),
                    }}
                    name={name}
                    options={options}
                    value={selected}
                    isMultiple
                    hideSelectedOptions={false}
                    placeholder={title}
                    customStyle={{
                        ...customStyles,
                        ...overrideSelectStyle,
                    }}
                    isClearable={false}
                    isSortOption={isSortOption}
                    footer={
                        <FooterContainer>
                            <StyledDivider />
                            <StyledButton
                                data-name={`${name}-select-action-button`}
                                name={actionButtonName}
                                onClick={() => {
                                    onChange && onChange(selected);
                                }}
                            />
                        </FooterContainer>
                    }
                    onChange={onChange}
                />
            </Container>
        </UseTitleContext.Provider>
    );
};
export default FilterSelect;

const Container = styled.div<{ isBorderless?: boolean }>(
    ({ isBorderless }) => ({
        border: isBorderless
            ? 'none'
            : `1px solid ${aampeTheme.colors.interactiveWG01}`,
        borderRadius: isBorderless ? 'none' : aampeTheme.borderRadius.default,
        height: '36px',
        minWidth: isBorderless ? '' : '144px',
        boxSizing: 'border-box',
    }),
);

const StyledSearchableSelect = styled(AampeCreateSelectDropdown)({
    height: '36px',
    width: '100%',
    // zIndex: 200, removed temporarily
});

const FooterContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    padding: aampeTheme.spacing4(2),
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
});

const StyledDivider = styled(Divider)({
    width: '100%',
});

const StyledButton = styled(BasicButton)({
    width: '100%',
    backgroundColor: aampeTheme.colors.solidA01,
    '&:hover': {
        backgroundColor: aampeTheme.colors.solidA01,
        border: `1px solid ${aampeTheme.colors.solidA01}`,
    },
    border: `1px solid ${aampeTheme.colors.solidA01}`,
});
